import { createSignal } from "solid-js";

/**
 * Severity variants for toast notifications, matching UIAlert variants.
 */
export const NotificationVariant = Object.freeze({
  INFO: "info",
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error",
});

const DEFAULT_TIMEOUT_MS = 4000;
const ERROR_TIMEOUT_MS = 8000;

const [toasts, setToasts] = createSignal([]);

let nextId = 0;

/**
 * Remove a toast by id. No-op if it is already gone.
 * @param {number} id The toast id
 * @returns {void}
 */
export function dismiss(id) {
  setToasts((current) => current.filter((toast) => toast.id !== id));
}

/**
 * Push a toast and schedule its auto-dismissal.
 * @param {string} variant A NotificationVariant value
 * @param {string} message The message to display
 * @param {{ timeout?: number }} [opts] Options; timeout in ms, 0 to keep sticky
 * @returns {number} The id of the created toast
 */
function push(variant, message, opts = {}) {
  const id = ++nextId;
  const timeout =
    opts.timeout ??
    (variant === NotificationVariant.ERROR
      ? ERROR_TIMEOUT_MS
      : DEFAULT_TIMEOUT_MS);

  setToasts((current) => [...current, { id, variant, message }]);

  if (timeout > 0) {
    setTimeout(() => dismiss(id), timeout);
  }

  return id;
}

/**
 * Raise transient toast notifications. Callable from anywhere, including
 * non-component code, since the underlying store is module-level.
 */
export const notify = {
  info: (message, opts) => push(NotificationVariant.INFO, message, opts),
  success: (message, opts) => push(NotificationVariant.SUCCESS, message, opts),
  warning: (message, opts) => push(NotificationVariant.WARNING, message, opts),
  error: (message, opts) => push(NotificationVariant.ERROR, message, opts),
};

export { toasts };
